import {lazy, Suspense} from 'react'
import {ThemeProvider, Box, Grid} from '@primer/react-brand/esm'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'

import {ContentfulFaqGroup} from '@github-ui/swp-core/components/contentful/ContentfulFaqGroup'
import {ContentfulInlineFootnotesList} from '@github-ui/swp-core/components/contentful/ContentfulInlineFootnotesList'
import {ContentfulSubnav} from '@github-ui/swp-core/components/contentful/ContentfulSubnav'
import {FootnotesProvider} from '@github-ui/swp-core/components/contentful/FootnotesContext'
import {useNinetailedConfig} from '@github-ui/swp-core/contexts/NinetailedConfigContext'
import {useContentfulData} from '@github-ui/swp-core/lib/hooks/use-contentful-data'
import type {NinetailedExperienceEntry} from '@github-ui/swp-core/lib/utils/contentful-ninetailed'

import {toPayload} from '../../../../lib/types/payload'
import {toBrandPage} from '../../../../brand/lib/types/contentful'
import type {GenericSectionWithIds} from '../../../../brand/lib/types/contentful'
import {useDomReady} from '../../../../lib/utils/platform'

import {PlanTypeContextProvider} from '../_context/PlanTypeContext'

import {CompareTable} from './CompareTable'
import {CopilotPlansExperimentSkeleton} from './CopilotPlansExperimentSkeleton'
import {CtaSection} from './CtaSection'
import {HeroSection} from './HeroSection'
import {VendorPixelTracker} from '../../../../components/VendorPixelTracker'

const NinetailedPlansHeroSection = lazy(() => import('./NinetailedPlansHeroSection'))

type PlansHeroSectionProps = {
  contentfulContent: GenericSectionWithIds
}

export function PlansHeroSection({contentfulContent}: PlansHeroSectionProps) {
  const isDomReady = useDomReady()
  const ninetailedConfig = useNinetailedConfig()
  const baseline = <HeroSection contentfulContent={contentfulContent} />
  const rawExperiences: NinetailedExperienceEntry[] =
    isFeatureEnabled('landing_pages_ninetailed') && Array.isArray(contentfulContent.fields.nt_experiences)
      ? contentfulContent.fields.nt_experiences
      : []

  if (ninetailedConfig === null || rawExperiences.length === 0) {
    return baseline
  }

  if (!isDomReady) {
    return <CopilotPlansExperimentSkeleton />
  }

  return (
    <ErrorBoundary boundaryName="ninetailed-plans-hero-section" fallback={baseline}>
      <Suspense fallback={<CopilotPlansExperimentSkeleton />}>
        <NinetailedPlansHeroSection
          contentfulContent={contentfulContent}
          ninetailedConfig={ninetailedConfig}
          rawExperiences={rawExperiences}
        />
      </Suspense>
    </ErrorBoundary>
  )
}

export default function FeaturesCopilotPlansIndex() {
  const {contentfulRawJsonResponse, vendorPixels} = toPayload(useRoutePayload<unknown>())
  const contentfulResponse = useContentfulData(contentfulRawJsonResponse)
  const page = toBrandPage(contentfulResponse)

  const {template} = page.fields
  const {subnav, faq} = template.fields
  const {featuresCopilotPlansComparison, featuresCopilotPlansCtaSection, featuresCopilotPlansHeroSection} =
    page.ids as {
      featuresCopilotPlansComparison: GenericSectionWithIds
      featuresCopilotPlansCtaSection: GenericSectionWithIds
      featuresCopilotPlansHeroSection: GenericSectionWithIds
    }

  return (
    <VendorPixelTracker vendorPixels={vendorPixels ?? []}>
      <FootnotesProvider>
        <PlanTypeContextProvider>
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <ThemeProvider colorMode="dark" className="lp-Copilot position-relative">
            {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
            <Box className="lp-SubNav-spacer" />

            {subnav ? (
              /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
              <ContentfulSubnav component={subnav} className="lp-Hero-subnav lp-Hero-subnav--highContrast" />
            ) : null}

            {featuresCopilotPlansHeroSection ? (
              <PlansHeroSection contentfulContent={featuresCopilotPlansHeroSection} />
            ) : null}

            {featuresCopilotPlansComparison ? (
              <CompareTable contentfulContent={featuresCopilotPlansComparison} />
            ) : null}

            {featuresCopilotPlansCtaSection ? <CtaSection contentfulContent={featuresCopilotPlansCtaSection} /> : null}

            {faq ? (
              /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
              <section id="faq" className="lp-Section tmp-pb-8 tmp-pb-md-12 tmp-pt-8 tmp-pt-md-12">
                <Grid>
                  {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
                  <Grid.Column span={12} className="lp-FAQs">
                    <ContentfulFaqGroup component={faq} />
                  </Grid.Column>
                </Grid>
              </section>
            ) : null}

            <Grid>
              <Grid.Column span={12}>
                <ContentfulInlineFootnotesList />
              </Grid.Column>
            </Grid>
          </ThemeProvider>
        </PlanTypeContextProvider>
      </FootnotesProvider>
    </VendorPixelTracker>
  )
}
